import {
	ContextMenu,
	ContextMenuItem,
	IDataListColumn,
	IconButton,
	Id,
	Loading,
	PaginatedAutoDataList,
	PaginatedListFetcherConfig,
	laravelPaginatedListFetchConfig,
	useFormatDateTime,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ContactFilterApplyDrawer, ContactFilterBar } from "../../../contactFilter";
import { useContactFilter } from "../../../contactFilter/useContactFilter";
import { useContactListUtils } from "../../../contacts/useContactListUtils";
import { LocalContactViewSwitcher } from "../../../contactViews/local/LocalContactViewSwitcher";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";
import { useContactView } from "../../../shared/hooks/useContactView";
import { ContactUpdateInquiryCompletedIndexResource } from "../../../shared/types/contactUpdateInquiry";

interface Props {
	contactUpdateInquiryId: Id;
}

export const CompletedUpdateInquiryContactList: FC<Props> = ({ contactUpdateInquiryId }) => {
	const { t } = useTranslation();
	const { currentContactView, isLoading: contactViewLoading } = useContactView();
	const { columns } = useContactListUtils(currentContactView);
	const navigate = useNavigate();
	const { onApplyContactFilter, openContactFilterDrawer, activeContactFilterId, applyFilterDrawerProps } =
		useContactFilter(contactUpdateInquiryService.contactsWithCompletedInquiryKey(contactUpdateInquiryId));
	const formatDateTime = useFormatDateTime();

	const queryKey = useMemo(() => {
		const baseKey = [
			...contactUpdateInquiryService.contactsWithCompletedInquiryKey(contactUpdateInquiryId),
			contactUpdateInquiryService.contactsWithInquiryKeySuffix,
			`${activeContactFilterId}`,
		];

		if (currentContactView?.id) {
			return baseKey.concat(`${currentContactView.id}`);
		}

		return baseKey;
	}, [activeContactFilterId, contactUpdateInquiryId, currentContactView]);

	const dataListColumns: IDataListColumn<ContactUpdateInquiryCompletedIndexResource>[] = useMemo(() => {
		return [
			{
				heading: t("Completed at"),
				valueKey: "completedAt",
				type: "value",
				render: (inquiry) => formatDateTime(inquiry.completedAt),
			},
			{
				heading: t("Status"),
				valueKey: "status",
				type: "value",
				render: (inquiry) => t(inquiry.status),
			},
			{
				heading: t("Inquiry date"),
				valueKey: "inquiryDate",
				type: "value",
				render: (inquiry) => formatDateTime(inquiry.inquiryDate),
			},
			...(columns as IDataListColumn<ContactUpdateInquiryCompletedIndexResource>[]),
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("View contact")}
							label={t("View contact")}
							action={() => navigate(`/contacts/active/${id}`)}
							icon="eye"
							shortcut="primary"
						/>
					</ContextMenu>
				),
			},
		];
	}, [columns, formatDateTime, navigate, t]);

	const initialSortingAttribute =
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "lastName") ??
		currentContactView?.contactAttributes.find((attribute) => attribute.entityType === "createdAt");

	if (contactViewLoading) {
		return <Loading />;
	}

	return (
		<>
			<PaginatedAutoDataList
				name="completedFeedbacks"
				empty={t("No feedbacks available.")}
				headerChildren={
					<ContactFilterBar
						contactFilterId={activeContactFilterId}
						onApply={onApplyContactFilter}
						onClick={openContactFilterDrawer}
					/>
				}
				heading={t("Completed feedback")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					queryKey,
					(config: PaginatedListFetcherConfig<ContactUpdateInquiryCompletedIndexResource>) =>
						contactUpdateInquiryService.contactsWithCompletedInquiry(
							config,
							activeContactFilterId,
							currentContactView?.id || null,
							contactUpdateInquiryId,
						),
				)}
				headerActions={
					<>
						<LocalContactViewSwitcher />
						<IconButton size="small" title={t("Contact filter")} icon="filter" onClick={openContactFilterDrawer} />
					</>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ name: "firstName", label: t("First name") },
						{ name: "lastName", label: t("Last name") },
						{ name: "email", label: t("Email") },
						{ name: "phone", label: t("Phone") },
						{ name: "company", label: t("Company") },
					],
				}}
				onRowClick={({ id }) => navigate(`/contacts/active/${id}`)}
				enableBulkEdit={false}
				initialSorting={
					initialSortingAttribute ? { column: initialSortingAttribute.id as any, direction: "asc" } : null
				}
			/>
			{applyFilterDrawerProps.editEntityId != null && (
				<ContactFilterApplyDrawer
					{...applyFilterDrawerProps}
					contactFilterId={activeContactFilterId}
					onApply={onApplyContactFilter}
				/>
			)}
		</>
	);
};
